import { useMemo, useState } from 'react';
import {
  Accordion,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type {
  EventStatus,
  FleetDueSummary,
  MaintenanceArea,
  MaintenanceEventList,
} from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate } from '../../lib/format';
import { DUE_STATUS_COLORS } from '../../theme';
import { DueItemsTable } from './DueItemsTable';
import { MaintenanceEventDrawer } from './MaintenanceEventDrawer';

export function MaintenancePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/$org/maintenance' });
  const { organisation, can } = useSession();
  const org = organisation!.slug;

  const [eventId, setEventId] = useState<number | null>(null);
  const [isEventOpen, setEventOpen] = useState(false);

  const tab = search.tab ?? 'due';
  const setTab = (value: string | null) =>
    void navigate({ to: '/$org/maintenance', params: { org }, search: { tab: value ?? 'due' } });

  const fleet = useQuery({
    queryKey: ['maintenance', 'fleet', org],
    queryFn: async () => unwrap(await api.GET('/api/v1/maintenance/due/fleet/')),
    enabled: tab === 'due',
  });

  const openEvent = (id: number | null) => {
    setEventId(id);
    setEventOpen(true);
  };

  return (
    <>
      <PageHeader
        title={t('maintenance.fleetOverview')}
        subtitle={t('maintenance.dueList')}
        actions={
          can('maintenance.add_event') ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => openEvent(null)}>
              {t('maintenance.newEvent')}
            </Button>
          ) : null
        }
      />

      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="due">{t('maintenance.dueList')}</Tabs.Tab>
          <Tabs.Tab value="log">{t('maintenance.log')}</Tabs.Tab>
          <Tabs.Tab value="templates">{t('maintenance.templates')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'due' ? (
        <FleetDueList
          rows={fleet.data ?? []}
          isLoading={fleet.isLoading}
          onOpenAircraft={(registration) =>
            void navigate({
              to: '/$org/aircraft/$registration/maintenance',
              params: { org, registration },
            })
          }
        />
      ) : null}

      {tab === 'log' ? <MaintenanceLog onOpenEvent={openEvent} /> : null}
      {tab === 'templates' ? <TaskTemplateList /> : null}

      <MaintenanceEventDrawer
        opened={isEventOpen}
        onClose={() => setEventOpen(false)}
        eventId={eventId}
      />
    </>
  );
}

/** The fleet due list, one aircraft per panel, worst first and expanded by default. */
function FleetDueList({
  rows,
  isLoading,
  onOpenAircraft,
}: {
  rows: FleetDueSummary[];
  isLoading?: boolean;
  onOpenAircraft: (registration: string) => void;
}) {
  const { t } = useTranslation();

  if (isLoading) return <Loader />;
  if (rows.length === 0) {
    return (
      <Card p="md">
        <EmptyState title={t('maintenance.OK')} hint={t('maintenance.dueList')} />
      </Card>
    );
  }

  return (
    <Accordion
      variant="separated"
      multiple
      defaultValue={rows
        .filter((row) => row.worst_status === 'OVERDUE' || row.blocking_count > 0)
        .map((row) => String(row.aircraft))}
    >
      {rows.map((row) => (
        <Accordion.Item key={row.aircraft} value={String(row.aircraft)}>
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap" pr="sm">
              <Group gap="sm" wrap="nowrap">
                <Text fw={700} className="identifier">
                  {row.registration}
                </Text>
                <StatusBadge kind="aircraft" value={row.status} size="sm" />
              </Group>
              <Group gap="xs" wrap="nowrap">
                {row.blocking_count ? (
                  <Badge color="red" variant="filled" size="sm">
                    {row.blocking_count} × {t('maintenance.groundsAircraft')}
                  </Badge>
                ) : null}
                <Badge
                  color={DUE_STATUS_COLORS[row.worst_status as keyof typeof DUE_STATUS_COLORS]}
                  size="sm"
                >
                  {t(`maintenance.${row.worst_status}`)}
                </Badge>
                <Text fz="xs" c="dimmed" visibleFrom="sm">
                  {row.items.length} × {t('maintenance.task')}
                </Text>
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <DueItemsTable items={row.items} />
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => onOpenAircraft(row.registration)}
                style={{ alignSelf: 'flex-start' }}
              >
                {t('maintenance.programme')}
              </Button>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

function MaintenanceLog({ onOpenEvent }: { onOpenEvent: (id: number) => void }) {
  const { t } = useTranslation();
  const { enumChoices } = useSession();
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 250);
  const [area, setArea] = useState<MaintenanceArea | null>(null);
  const [status, setStatus] = useState<EventStatus | null>(null);
  const [page, setPage] = useState(1);

  const events = useQuery({
    queryKey: ['maintenance', 'events', debounced, area, status, page],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/events/', {
          params: {
            query: {
              search: debounced || undefined,
              area: area || undefined,
              status: status || undefined,
              page,
              page_size: 50,
              ordering: '-performed_on,-opened_on',
            },
          },
        }),
      ),
  });

  const columns = useMemo<ColumnDef<MaintenanceEventList, unknown>[]>(
    () => [
      {
        accessorKey: 'registration',
        header: t('aircraft.registration'),
        meta: { mono: true, width: 100 },
        cell: ({ row }) => (
          <Text fw={600} className="identifier">
            {row.original.registration}
          </Text>
        ),
      },
      {
        accessorKey: 'performed_on',
        header: t('maintenance.performedOn'),
        meta: { width: 110 },
        cell: ({ row }) =>
          formatDate(row.original.performed_on) || formatDate(row.original.opened_on),
      },
      { accessorKey: 'title', header: t('common.name') },
      {
        accessorKey: 'area',
        header: t('maintenance.area'),
        meta: { width: 130 },
        cell: ({ row }) => (row.original.area ? t(`maintenance.${row.original.area}`) : ''),
      },
      {
        accessorKey: 'performed_by_name',
        header: t('maintenance.performedBy'),
        meta: { width: 170 },
        cell: ({ row }) =>
          row.original.performed_by_name || row.original.performed_by_organisation,
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        meta: { width: 120 },
        cell: ({ row }) => <StatusBadge kind="event" value={row.original.status} />,
      },
    ],
    [t],
  );

  return (
    <>
      <Group mb="sm" gap="sm" wrap="wrap">
        <TextInput
          placeholder={t('action.search')}
          leftSection={<IconSearch size={15} />}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          w={220}
        />
        <Select
          placeholder={t('maintenance.area')}
          data={enumChoices('maintenance_area').map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          value={area}
          onChange={(value) => setArea(value as MaintenanceArea | null)}
          clearable
          w={170}
        />
        <Select
          placeholder={t('common.status')}
          data={enumChoices('event_status').map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          value={status}
          onChange={(value) => setStatus(value as EventStatus | null)}
          clearable
          w={160}
        />
      </Group>
      <DataTable
        data={events.data?.results ?? []}
        columns={columns}
        isLoading={events.isLoading}
        onRowClick={(row) => onOpenEvent(row.id)}
        pagination={{
          page,
          pageSize: 50,
          total: events.data?.count ?? 0,
          onPageChange: setPage,
        }}
        emptyTitle={t('maintenance.log')}
        minWidth={900}
      />
    </>
  );
}

/**
 * The seeded check templates a club clones into an aircraft's programme. Read-only here: they
 * are edited in the Unfold admin, which is where configuration lives.
 */
function TaskTemplateList() {
  const { t } = useTranslation();

  const templates = useQuery({
    queryKey: ['maintenance', 'templates'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/templates/', {
          params: { query: { page_size: 200, ordering: 'sort_order,code' } },
        }),
      ),
  });

  return (
    <Stack gap="md">
      <Text fz="sm" c="dimmed">
        {t('maintenance.populateHint')}
      </Text>
      {templates.isLoading ? (
        <Loader size="sm" />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {(templates.data?.results ?? []).map((template) => (
            <Card key={template.id} p="md">
              <Group justify="space-between" mb={4} wrap="nowrap">
                <Text fw={600} fz="sm">
                  {template.code}
                </Text>
                <Badge size="xs" variant="light">
                  {t(`maintenance.${template.area}`)}
                </Badge>
              </Group>
              <Text fz="sm">{template.name}</Text>
              <Text fz="xs" c="dimmed" mt={4} ff="monospace">
                {template.interval_display}
              </Text>
              {template.is_system ? (
                <Badge size="xs" color="gray" variant="outline" mt="xs">
                  {t('settings.globalEntry')}
                </Badge>
              ) : null}
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
